import PolicyPage from "../components/PolicyPage";

export default function Terms() {
  return (
    <PolicyPage
      testId="terms-page"
      eyebrow="Sumanya · Legal"
      title="Terms & Conditions"
      subtitle="Welcome to Sumanya, the online jewellery brand of Suman Jewellers, a trusted jewellery business serving customers since 1991."
      updated="February 2026"
      sections={[
        {
          body: "By accessing or using the Sumanya website, placing an order, or purchasing any product through our website, you agree to the following Terms & Conditions.",
        },
        {
          heading: "1. Product information",
          body: [
            "We make every effort to ensure that the product images, descriptions, weights, dimensions, colours and other information displayed on our website are accurate.",
            "However, jewellery is handcrafted and may have minor variations in finish, texture, dimensions or weight. Such minor variations are inherent to jewellery manufacturing and should not be considered a manufacturing defect.",
            "The actual colour and appearance of a product may also vary slightly depending on your screen or device.",
          ],
        },
        {
          heading: "2. Jewellery purity",
          body: [
            "All applicable gold jewellery is sold according to the purity mentioned in the product description and invoice.",
            "For 22K jewellery, the applicable purity is 916 (91.60% gold), and for 18K jewellery, the applicable purity is 750 (75% gold), unless otherwise specifically mentioned.",
            "Our guarantee relates to the stated purity of the jewellery. Jewellery should be handled with appropriate care, particularly lightweight, delicate and fancy designs.",
          ],
        },
        {
          heading: "3. Order confirmation",
          body: [
            "An order placed on the website constitutes a request to purchase the selected product.",
            "Orders are subject to product availability, successful payment and verification of the order details. We reserve the right to cancel or decline an order in exceptional circumstances, including pricing errors, inventory discrepancies, payment issues or suspected fraudulent transactions.",
            "In the event of cancellation by us, any amount paid by the customer for the cancelled order will be refunded through the applicable payment method.",
          ],
        },
        {
          heading: "4. Product inspection upon delivery",
          body: [
            "Customers are advised to carefully inspect the package and jewellery immediately upon delivery.",
            "If the package appears tampered with, damaged or opened, please record the condition of the package and contact us immediately.",
            "Once the jewellery has been received and accepted by the customer, claims relating to product weight, design or physical condition may not be entertained unless the issue has been reported within the applicable return / exchange period and is covered under our policy.",
          ],
        },
        {
          heading: "5. Returns & exchange",
          body: "If a customer does not like the jewellery purchased online, the product may be eligible for exchange within 7 days of delivery, subject to the following conditions:",
          bullets: [
            "The jewellery must be unused and in its original condition.",
            "The jewellery must not have any breakage, damage, scratches, alteration, dirt or other signs of use.",
            "All original packaging, documents and certificates supplied with the product must be retained.",
            "The original invoice / order details must be available.",
            "Products specifically marked as non-returnable / non-exchangeable will not qualify.",
          ],
          note: "A non-refundable return initiation fee of ₹250 per shipment will be deducted from the refund / exchange value once a return is approved. This fee covers secure reverse-logistics pickup, insurance and quality inspection. It does not apply to returns arising from damage in transit or a proven manufacturing defect. Exchange eligibility is subject to inspection and approval by Sumanya. Please refer to our Shipping & Returns page for the complete procedure.",
        },
        {
          heading: "6. Exchange after 7 days",
          body: "After the initial 7-day exchange period, eligible 916 and 750 gold jewellery may be accepted for exchange according to the applicable exchange policy. The gold value will be calculated based on the prevailing applicable gold/MCX rate and the applicable purity:",
          bullets: [
            "916 jewellery: 91.60% gold value.",
            "750 jewellery: 75.00% gold value.",
          ],
          note: "Making charges are non-refundable / non-exchangeable. Any applicable deductions, charges, stone values or other components will be calculated according to the product and the applicable exchange policy.",
        },
        {
          heading: "7. Cash refund",
          body: [
            "Where a product is eligible for a cash refund under our applicable policy, an additional 2% deduction may be applicable.",
            "Where cash refund is not possible or where the original payment method is required, the refund may be processed through the original payment method. Such refunds may take approximately 14–15 business days, depending on the payment gateway, bank or financial institution.",
          ],
        },
        {
          heading: "8. Diamond jewellery",
          body: [
            "Diamond jewellery is subject to separate exchange terms.",
            "Eligible diamond jewellery may be exchanged after deduction of 10% of the diamond value and applicable making charges, subject to the applicable exchange policy.",
            "The original diamond certificate / certificate card supplied with the jewellery must be presented / returned for an exchange request. No exchange request for certified diamond jewellery will be entertained if the required original certificate is unavailable, where such certificate was originally supplied with the product.",
          ],
        },
        {
          heading: "9. Gemstones",
          body: [
            "Gemstones, including precious and semi-precious gemstones, are not eligible for return or exchange.",
            "Where a gemstone is mounted in gold jewellery, the applicable gold portion may be considered for exchange in accordance with our exchange policy. The value of the gemstone itself will not be included in the exchange value unless specifically stated otherwise for a particular product.",
          ],
        },
        {
          heading: "10. Silver jewellery",
          body: [
            "Silver is a naturally reactive metal and may develop blackening, tarnishing or discolouration due to exposure to air, moisture, perspiration, chemicals, cosmetics, hair products, perfumes, hair dye and other environmental factors.",
            "Such tarnishing is a natural characteristic of silver and does not indicate any deficiency in purity. Silver jewellery should therefore be stored properly and cleaned according to our Care Guide.",
          ],
        },
        {
          heading: "11. Delicate & lightweight jewellery",
          body: [
            "Lightweight and fancy jewellery may be delicate by nature.",
            "Breakage, bending or damage caused by accidental impact, improper handling, excessive force, snagging, pulling, wearing during physical activities or normal wear and tear is not covered by any product guarantee.",
            "Customers are advised to select designs appropriate for their intended use and to follow our jewellery care recommendations.",
          ],
        },
        {
          heading: "12. Lifetime repair & cleaning",
          body: [
            "We provide lifetime free jewellery repair and cleaning for eligible jewellery purchased from Sumanya / Suman Jewellers.",
            "Routine repair and cleaning are provided free of service charges.",
            "However, the cost of any additional gold, silver, stones or other materials required for the repair will be charged separately. Damage caused by misuse, accidents or external factors may require additional material or restoration charges.",
          ],
        },
        {
          heading: "13. Pricing & product components",
          body: "The net price of a jewellery product may include, as applicable:",
          bullets: [
            "Gold / silver value.",
            "Wastage.",
            "Grinding and polishing charges.",
            "Making charges.",
            "Hallmarking charges.",
            "IGI / GIA or other certification charges.",
            "Precious and non-precious stones.",
            "Other material / components.",
            "Packaging charges.",
            "GST and other applicable taxes.",
          ],
          note: "A detailed price breakup may be provided where applicable.",
        },
        {
          heading: "14. PAN & KYC requirements",
          body: [
            "For purchases or payments of ₹2,00,000 or more in a single day, customers may be required to provide a self-attested copy of their PAN card and/or other KYC documents as required under applicable laws and regulations.",
            "Additional information or documentation may be requested where legally required.",
          ],
        },
        {
          heading: "15. Original invoice & documents",
          body: [
            "Customers are advised to retain their original invoice and all certificates/documentation supplied with the jewellery.",
            "Where an original invoice, certificate or other required document is specifically required under an applicable policy, the relevant request may not be processed without it.",
          ],
        },
        {
          heading: "16. Payment",
          body: [
            "Payments must be made through the payment methods made available on the Sumanya website.",
            "Orders will generally be processed only after successful confirmation of payment.",
            "We do not request customers to share their card PIN, OTP, CVV or banking passwords with us.",
          ],
        },
        {
          heading: "17. Fraud prevention",
          body: [
            "For security and fraud-prevention purposes, we may verify an order, payment or customer information before dispatch.",
            "If an order appears suspicious, fraudulent or inconsistent with the information provided, we reserve the right to hold, cancel or decline the order.",
          ],
        },
        {
          heading: "18. Changes to terms",
          body: [
            "Sumanya reserves the right to modify these Terms & Conditions from time to time.",
            "Any updated terms will be published on this page and will apply to orders placed after the updated terms become effective.",
          ],
        },
        {
          heading: "19. Contact us",
          body: [
            "For any questions regarding an order, product, exchange, return or these Terms & Conditions, please contact us.",
            "Sumanya · Suman Jewellers, Sahebganj, Khanua, Chapra, Bihar – 841301, India.",
            "Email: info@sumanya.co · Phone: +91-8210981248",
          ],
        },
      ]}
    />
  );
}
